import React, { useState, useMemo } from 'react';
import { MdVpnKey, MdHistory, MdEdit } from 'react-icons/md';
import useToastWithSound from '../../hooks/useToastWithSound';
import api from '../../ui/api';
import { codeText } from './accessCodeText';
import useData from '../useData';
import PageShell, { Button } from '../../ui/PageShell';
import { formatPhone } from '../../ui/phone';
import Tabs from '../../ui/Tabs';
import Table from '../../ui/Table';
import { ConfirmModal } from '../../ui/Modal';
import UserAccessCodeModal from './UserAccessCodeModal';
import UserLoginHistoryModal from './UserLoginHistoryModal';
import OfficeUserModal from './OfficeUserModal';
import HelpButton from '../help/HelpButton';
import './users.css';

const ROLE_LABEL = { admin: 'مدير', register: 'موجه', teacher: 'معلم', student: 'طالب' };
const fmt = (iso) => (iso ? String(iso).slice(0, 16).replace('T', ' ') : '—');
const isOfficeRole = (r) => r === 'admin' || r === 'register';

// Which accounts each tab lists.
const GROUPS = [
  { id: 'office', label: 'الإدارة', test: (u) => isOfficeRole(u.role) },
  { id: 'teacher', label: 'المعلمون', test: (u) => u.role === 'teacher' },
  { id: 'student', label: 'الطلاب وأولياء الأمور', test: (u) => u.role === 'student' },
  { id: 'all', label: 'الكل', test: () => true },
];
const PARENT_LABEL = { father: 'الأب', mother: 'الأم', none: '—' };

/** Every login identity in the system: office, teachers, and every
 *  student/parent account. Lock/unlock, plus the two things the office
 *  actually needs day to day — issuing a رمز دخول for someone SMS
 *  can't reach, and reading a person's login history when they say
 *  "ما بيوصلني الكود" or "حدا داخل عحسابي". */
export default function Users() {
  const toast = useToastWithSound();
  const { data, loading, error, reload } = useData(() => api.users(), []);
  const { data: codes, reload: reloadCodes } = useData(() => api.accessCodes(), []);

  const [target, setTarget] = useState(null);     // lock/unlock confirm
  const [codeFor, setCodeFor] = useState(null);   // access-code dialog
  const [historyFor, setHistoryFor] = useState(null);
  const [editing, setEditing] = useState(null);   // null | 'new' | office user row
  const [group, setGroup] = useState('office');
  const [busy, setBusy] = useState(false);

  // user_id -> its active access code row, so the table can show a
  // "لديه رمز" column without a request per row.
  const codeByUser = useMemo(() => {
    const m = new Map();
    for (const c of codes || []) m.set(String(c.user_id), c);
    return m;
  }, [codes]);

  const rows = useMemo(
    () => (data || []).map((u) => {
      const code = codeByUser.get(String(u.id));
      return {
        ...u,
        role_label: ROLE_LABEL[u.role] || u.role,
        parent_label: PARENT_LABEL[u.parent_type] || '—',
        status_label: u.is_locked ? 'موقوف' : 'نشط',
        // Precomputed rather than derived from the row inside a
        // formatter: Table's filter dropdown calls formatValue with one
        // argument only, so a row-dependent formatter crashes there.
        code_label: code ? codeText(code.code_hint) : '—',
        code_uses: code ? code.use_count : null,
      };
    }),
    [data, codeByUser]
  );
  const counts = useMemo(
    () => Object.fromEntries(GROUPS.map((g) => [g.id, rows.filter(g.test).length])),
    [rows]
  );
  const shown = useMemo(() => rows.filter(GROUPS.find((g) => g.id === group).test), [rows, group]);

  const toggleLock = async () => {
    setBusy(true);
    try {
      if (target.is_locked) await api.unlockUser(target.id);
      else await api.lockUser(target.id, 'إيقاف من لوحة الإدارة');
      toast.success(target.is_locked ? 'تم فك الإيقاف' : 'تم إيقاف الحساب');
      setTarget(null);
      reload();
    } catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  return (
    <PageShell
      title="المستخدمون"
      description="حسابات الدخول: المدراء والسكرتارية والمعلمون والطلاب وأولياء الأمور"
      actions={(
        <>
          <HelpButton guides={['user-add']} />
          <Button onClick={() => setEditing('new')} data-guide="users-add">+ مستخدم جديد</Button>
        </>
      )}
    >
      {error && <div className="ui-notice ui-notice-danger"><div className="ui-notice-body">{error}</div></div>}

      <Tabs
        active={group}
        onChange={setGroup}
        items={GROUPS.map((g) => ({ id: g.id, label: g.label, badge: counts[g.id] || null }))}
      />

      <Table
        keyField="id"
        rows={shown}
        loading={loading}
        emptyText="لا يوجد مستخدمون"
        searchPlaceholder="ابحث بالاسم أو الهاتف..."
        searchableKeys={['name', 'phone']}
        exportName="users"
        renderActions={(u) => (
          <>
            {isOfficeRole(u.role) && (
              <button type="button" className="ui-icon-btn" title="تعديل" onClick={() => setEditing(u)}>
                <MdEdit size={15} />
              </button>
            )}
            <button
              type="button"
              className="ui-icon-btn"
              title="رمز الدخول"
              onClick={() => setCodeFor(u)}
            >
              <MdVpnKey size={15} />
            </button>
            <button
              type="button"
              className="ui-icon-btn"
              title="سجل الدخول"
              onClick={() => setHistoryFor(u)}
            >
              <MdHistory size={15} />
            </button>
            <button
              type="button"
              className={`ui-btn-mini${u.is_locked ? '' : ' ui-btn-mini-warn'}`}
              onClick={() => setTarget(u)}
            >
              {u.is_locked ? 'فك الإيقاف' : 'إيقاف'}
            </button>
          </>
        )}
        columns={[
          { key: 'name', label: 'الاسم', width: 2, filterable: false, render: (r) => <strong>{r.name}</strong> },
          { key: 'phone', label: 'الهاتف', nowrap: true, filterable: false, render: (r) => (r.phone ? <bdi dir="ltr">{formatPhone(r.phone)}</bdi> : '—') },
          { key: 'role_label', label: 'النوع', nowrap: true, align: 'center', badge: () => 'info' },
          { key: 'parent_label', label: 'الصفة', nowrap: true, align: 'center' },
          { key: 'last_seen_at', label: 'آخر نشاط', nowrap: true, filterable: false, formatValue: fmt },
          {
            key: 'code_label',
            label: 'رمز الدخول',
            nowrap: true,
            align: 'center',
            badge: (v) => (v === '—' ? 'neutral' : 'warning'),
          },
          {
            key: 'status_label',
            label: 'الحالة',
            nowrap: true,
            align: 'center',
            badge: (v) => (v === 'موقوف' ? 'danger' : 'success'),
          },
        ]}
      />

      <ConfirmModal
        open={!!target}
        title={target?.is_locked ? 'فك إيقاف الحساب' : 'إيقاف الحساب'}
        tone={target?.is_locked ? 'success' : 'danger'}
        message={target?.is_locked
          ? `سيتمكن «${target?.name}» من تسجيل الدخول مجدداً.`
          : `لن يتمكن «${target?.name}» من تسجيل الدخول، وستُلغى جلساته الحالية فوراً.`}
        confirmText={target?.is_locked ? 'فك الإيقاف' : 'إيقاف'}
        busy={busy}
        onCancel={() => setTarget(null)}
        onConfirm={toggleLock}
      />

      {codeFor && (
        <UserAccessCodeModal
          // Remount per user AND refetch on open: use_count/last_used_at
          // change every time that person logs in, so a cached row from
          // page load would show stale numbers the moment they use it.
          key={codeFor.id}
          user={codeFor}
          existing={codeByUser.get(String(codeFor.id)) || null}
          api={api}
          toast={toast}
          onIssued={reloadCodes}
          onClose={() => { setCodeFor(null); reloadCodes(); }}
        />
      )}

      {editing && (
        <OfficeUserModal
          key={editing === 'new' ? 'new' : editing.id}
          user={editing === 'new' ? null : editing}
          api={api}
          toast={toast}
          onSaved={() => { setEditing(null); reload(); }}
          onClose={() => setEditing(null)}
        />
      )}

      {historyFor && (
        <UserLoginHistoryModal
          user={historyFor}
          api={api}
          onClose={() => setHistoryFor(null)}
        />
      )}
    </PageShell>
  );
}
